'use client';

import {
  Chip,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SavedQuery } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface EditSavedQueryDialogProps {
  workspaceId: string;
  savedQuery: SavedQuery;
  open: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditSavedQueryDialog = ({
  workspaceId,
  savedQuery,
  open,
  onClose,
  onUpdated,
}: EditSavedQueryDialogProps) => {
  const dal = useDAL();
  const [name, setName] = useState(savedQuery.name);
  const [description, setDescription] = useState(savedQuery.description ?? '');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(savedQuery.tags ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await dal.lakehouse.updateSavedQuery(workspaceId, savedQuery.id, {
        name: name.trim(),
        query: savedQuery.query,
        ...(description.trim() && { description: description.trim() }),
        tags,
      });
      onUpdated();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update saved query'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Edit Saved Query',
        onClose,
      }}
      footer={{
        primaryButton: {
          text: 'Save',
          onClick: handleSave,
          'data-testid': 'save-edit-saved-query-btn',
          disabled: isLoading || !name.trim(),
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: onClose,
          'data-testid': 'cancel-edit-saved-query-btn',
        },
      }}
    >
      <Stack gap='20px' padding='0 40px'>
        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Name *</Typography>
          </FormLabel>
          <TextField
            placeholder='Enter query name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error && !name.trim()}
            fullWidth
            size='small'
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Description</Typography>
          </FormLabel>
          <TextField
            placeholder='Enter description (optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>SQL Query</Typography>
          </FormLabel>
          <TextField
            value={savedQuery.query}
            fullWidth
            size='small'
            multiline
            rows={4}
            InputProps={{
              readOnly: true,
            }}
            sx={{
              '& .MuiInputBase-input': { fontFamily: 'monospace' },
              '& .MuiInputBase-root': {
                backgroundColor: 'var(--mui-palette-action-hover)',
              },
            }}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel>
            <Typography variant='subtitle1'>Tags</Typography>
          </FormLabel>
          <TextField
            placeholder='Type a tag and press Enter'
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            onBlur={handleAddTag}
            fullWidth
            size='small'
          />
          {tags.length > 0 && (
            <Stack direction='row' gap='4px' flexWrap='wrap' sx={{ mt: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size='small'
                  onDelete={() => handleRemoveTag(tag)}
                />
              ))}
            </Stack>
          )}
        </FormControl>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
